class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    window.addEventListener('load', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(
        content.querySelector('video, model-viewer, iframe'),
      );
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' },
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector('slideshow-component') &&
          this.classList.contains('complementary-products')
        ) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

// customElements.define('product-recommendations', ProductRecommendations);

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');

    this.form.addEventListener('submit', this.submitForm.bind(this));
    this.form.addEventListener('change', this.changeForm.bind(this));

    this.changeForm();
  }

  changeForm() {
    // check if has variants and if has variants check if there is a variant selected
    const variants = this.querySelectorAll('input[name="id"]');
    const variantSelected = this.querySelector('input[name="id"]:checked');
    this.submitButton.disabled = variants.length > 1 && !variantSelected;
  }

  submitForm(e) {
    e.preventDefault();
    const formData = new FormData(this.form);

    // render form data
    const formDataObject = {};
    formData.forEach((value, key) => {
      if (formDataObject[key]) {
        if (Array.isArray(formDataObject[key])) {
          formDataObject[key].push(value);
        }
        formDataObject[key] = value;
      } else {
        formDataObject[key] = value;
      }
    });

    // verify if the form is valid
    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    this.dataset.loading = true;
    this.submitButton.setAttribute('aria-busy', true);
    this.submitButton.setAttribute('aria-disabled', true);

    // submit the form
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formDataObject),
    })
      .then((response) => response.json())
      .then((data) => {
        // handle the response
        this.dataset.loading = false;
        this.dataset.added = true;
        this.submitButton.disabled = false;
        this.submitButton.setAttribute('aria-busy', false);
        this.submitButton.setAttribute('aria-disabled', false);

        const itemCount = document.querySelector('.navigation-menu-item--cart a span');

        console.log(itemCount);

        // update inventory
        document.dispatchEvent(
          new CustomEvent('inventory-updated', {
            detail: {
              item_count: (parseInt(itemCount.textContent) || 0) + 1,
            },
          }),
        );

        // fire custom event to update the cart
        document.dispatchEvent(new CustomEvent('update-cart'));

        setTimeout(() => {
          this.dataset.added = false;
        }, 2000);
      })
      .catch((error) => {
        console.error('Error:', error);
      });
    console.log(formData);
  }
}

customElements.define('product-form', ProductForm);

class ProductMediaZoom {
  constructor(element) {
    this.element = element;
    this.wrapper = element.querySelector('.product-media-zoom-wrapper');

    // Early return if required DOM elements are missing
    if (!this.wrapper) {
      return;
    }

    this.inner = this.wrapper.querySelector('.product-media-zoom-inner');
    this.img = this.inner?.querySelector('img');
    this.isZoomed = false;
    this.isPanning = false;
    this.hasMoved = false;
    this.currentX = 0;
    this.currentY = 0;
    this.zoomScale = 1;
    this.transitionTimeout = null;
    this.baseDimensions = null;
    this.wrapperDimensions = null;

    if (this.inner && !this.inner.style.transform) {
      this.inner.style.transform = 'translate3d(0px, 0px, 0px) scale(1)';
    }
    if (this.inner && !this.inner.style.transition) {
      this.inner.style.transition = 'transform 0s linear';
    }

    this.handleClick = this.handleClick.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.documentClickListenerAttached = false;

    element.addEventListener('click', this.handleClick);
    this.updateTransform();
  }

  handleClick(e) {
    // Don't zoom if user was panning
    if (this.hasMoved) {
      this.hasMoved = false;
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!this.isZoomed) {
      this.zoomIn(e);
    } else {
      this.zoomOut();
    }
  }

  zoomIn(e) {
    if (!this.wrapper || !this.img || !this.inner) return;

    this.isZoomed = true;
    this.element.setAttribute('data-zoomed', 'true');
    this.hasMoved = false;

    const initiateZoom = () => {
      const wrapperRect = this.wrapper.getBoundingClientRect();
      this.wrapperDimensions = {
        width: wrapperRect.width,
        height: wrapperRect.height,
      };

      const innerRect = this.inner.getBoundingClientRect();
      this.baseDimensions = {
        width: innerRect.width,
        height: innerRect.height,
      };

      this.clearTransitionReset();
      this.zoomScale = this.calculateZoomScale();
      this.setImageTransition('0.3s', 'ease');

      requestAnimationFrame(() => {
        this.updatePositionFromMouse(e);
        this.scheduleTransitionReset({ keepZero: true, delay: 300 });
      });
    };

    initiateZoom();

    if (ProductMediaZoom.activeInstance && ProductMediaZoom.activeInstance !== this) {
      ProductMediaZoom.activeInstance.zoomOut();
    }
    ProductMediaZoom.activeInstance = this;

    // Add mouse event listeners - track mouse position continuously
    this.wrapper.addEventListener('mousemove', this.handleMouseMove);
    this.wrapper.addEventListener('mouseleave', this.handleMouseLeave);

    // Add touch event listeners for mobile
    this.wrapper.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.wrapper.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.wrapper.addEventListener('touchend', this.handleTouchEnd);

    if (!this.documentClickListenerAttached) {
      document.addEventListener('click', this.handleDocumentClick);
      this.documentClickListenerAttached = true;
    }
  }

  zoomOut() {
    if (!this.wrapper || !this.img || !this.inner) return;

    this.isZoomed = false;
    this.element.removeAttribute('data-zoomed');

    this.clearTransitionReset();
    this.setImageTransition('0.3s', 'ease');

    // Reset transform and dimensions
    this.zoomScale = 1;
    this.currentX = 0;
    this.currentY = 0;
    this.hasMoved = false;
    this.baseDimensions = null;
    this.wrapperDimensions = null;
    this.updateTransform();

    // Remove mouse event listeners
    this.wrapper.removeEventListener('mousemove', this.handleMouseMove);
    this.wrapper.removeEventListener('mouseleave', this.handleMouseLeave);

    // Remove touch event listeners
    this.wrapper.removeEventListener('touchstart', this.handleTouchStart);
    this.wrapper.removeEventListener('touchmove', this.handleTouchMove);
    this.wrapper.removeEventListener('touchend', this.handleTouchEnd);

    if (this.documentClickListenerAttached) {
      document.removeEventListener('click', this.handleDocumentClick);
      this.documentClickListenerAttached = false;
    }

    if (ProductMediaZoom.activeInstance === this) {
      ProductMediaZoom.activeInstance = null;
    }

    this.scheduleTransitionReset({ keepZero: false, delay: 300 });
  }

  calculateZoomScale() {
    return 3;
  }

  setImageTransition(duration, easing = 'ease') {
    if (this.inner) {
      this.inner.style.transition = `transform ${duration} ${easing}`;
    }
  }

  scheduleTransitionReset({ keepZero = true, delay = 350 } = {}) {
    this.clearTransitionReset();
    this.transitionTimeout = window.setTimeout(() => {
      if (keepZero) {
        this.setImageTransition('0s', 'linear');
      } else {
        if (this.inner) {
          this.inner.style.transition = '';
        }
      }
      this.transitionTimeout = null;
    }, delay);
  }

  clearTransitionReset() {
    if (this.transitionTimeout) {
      window.clearTimeout(this.transitionTimeout);
      this.transitionTimeout = null;
    }
  }

  updatePositionFromMouse(e) {
    if (!this.isZoomed || !this.wrapper) return;

    const wrapperRect = this.wrapper.getBoundingClientRect();
    const wrapperWidth = this.wrapperDimensions?.width || wrapperRect.width || 1;
    const wrapperHeight = this.wrapperDimensions?.height || wrapperRect.height || 1;
    const baseWidth = this.baseDimensions?.width || wrapperWidth;
    const baseHeight = this.baseDimensions?.height || wrapperHeight;

    const scale = this.zoomScale;
    const scaledWidth = baseWidth * scale;
    const scaledHeight = baseHeight * scale;

    // Get mouse position relative to wrapper
    const mouseX = e.clientX - wrapperRect.left;
    const mouseY = e.clientY - wrapperRect.top;

    // Clamp mouse position to wrapper bounds
    const clampedX = Math.max(0, Math.min(wrapperRect.width, mouseX));
    const clampedY = Math.max(0, Math.min(wrapperRect.height, mouseY));

    const offsetX = clampedX - wrapperRect.width / 2;
    const offsetY = clampedY - wrapperRect.height / 2;

    this.currentX = -offsetX * (scale - 1);
    this.currentY = -offsetY * (scale - 1);

    const maxTranslateX = Math.max(0, (scaledWidth - wrapperRect.width) / 2);
    const maxTranslateY = Math.max(0, (scaledHeight - wrapperRect.height) / 2);

    this.currentX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.currentX));
    this.currentY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.currentY));

    this.updateTransform();
  }

  handleMouseMove(e) {
    if (!this.isZoomed) return;
    e.preventDefault();
    this.updatePositionFromMouse(e);
  }

  handleMouseLeave(e) {
    if (!this.isZoomed) return;
    // Optionally center or keep last position when mouse leaves
    // For now, we'll keep the last position
  }

  handleDocumentClick(e) {
    if (!this.isZoomed) return;
    if (!this.element.contains(e.target)) {
      this.zoomOut();
    }
  }

  handleTouchStart(e) {
    if (!this.isZoomed) return;
    const touch = e.touches[0];
    this.isPanning = false;
    this.hasMoved = false;
    this.lastTouchX = touch.clientX;
    this.lastTouchY = touch.clientY;
    this.touchStartX = this.currentX;
    this.touchStartY = this.currentY;
    this.scrollIntent = null; // 'horizontal', 'vertical', or null
    this.initialTouchX = touch.clientX;
    this.initialTouchY = touch.clientY;
  }

  handleTouchMove(e) {
    if (!this.isZoomed) return;
    
    const touch = e.touches[0];
    const currentTouchX = touch.clientX;
    const currentTouchY = touch.clientY;
    
    // Calculate movement from last touch position for more responsive panning
    const deltaX = currentTouchX - (this.lastTouchX || currentTouchX);
    const deltaY = currentTouchY - (this.lastTouchY || currentTouchY);
    const absDeltaX = Math.abs(currentTouchX - this.initialTouchX);
    const absDeltaY = Math.abs(currentTouchY - this.initialTouchY);

    // Determine scroll intent on first significant movement
    if (!this.scrollIntent && (absDeltaX > 5 || absDeltaY > 5)) {
      // If movement is primarily horizontal, allow parent scrolling
      // If movement is primarily vertical, handle panning
      this.scrollIntent = absDeltaX > absDeltaY ? 'horizontal' : 'vertical';
    }

    // Only handle panning for vertical movements
    if (this.scrollIntent === 'vertical') {
      e.preventDefault();
      this.isPanning = true;
      
      // Mark as moved if moved more than 5px from initial touch
      if (!this.hasMoved && (absDeltaX > 5 || absDeltaY > 5)) {
        this.hasMoved = true;
      }

      // Update position using relative movement from last touch (more responsive)
      this.currentX += deltaX;
      this.currentY += deltaY;

      // Update last touch position for next move event
      this.lastTouchX = currentTouchX;
      this.lastTouchY = currentTouchY;

      this.constrainPan();
      this.updateTransform();
    }
    // If horizontal, don't preventDefault - allow parent container to scroll
  }

  handleTouchEnd(e) {
    if (!this.isZoomed) return;
    // Immediately stop panning when touch ends
    this.isPanning = false;
    this.scrollIntent = null;
    this.lastTouchX = null;
    this.lastTouchY = null;
  }

  constrainPan() {
    // Get container dimensions
    if (!this.wrapper) return;

    const wrapperRect = this.wrapper.getBoundingClientRect();
    const wrapperWidth = this.wrapperDimensions?.width || wrapperRect.width || 1;
    const wrapperHeight = this.wrapperDimensions?.height || wrapperRect.height || 1;
    const baseWidth = this.baseDimensions?.width || wrapperWidth;
    const baseHeight = this.baseDimensions?.height || wrapperHeight;

    const scale = this.zoomScale;
    const maxTranslateX = Math.max(0, (baseWidth * scale - wrapperWidth) / 2);
    const maxTranslateY = Math.max(0, (baseHeight * scale - wrapperHeight) / 2);

    if (maxTranslateX > 0) {
      this.currentX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.currentX));
    } else {
      this.currentX = 0;
    }

    if (maxTranslateY > 0) {
      this.currentY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.currentY));
    } else {
      this.currentY = 0;
    }
  }

  updateTransform() {
    if (this.inner) {
      this.inner.style.transform = `translate3d(${this.currentX}px, ${this.currentY}px, 0) scale(${this.zoomScale})`;
    }
  }
}

ProductMediaZoom.activeInstance = null;

// Initialize product media zoom
function initializeProductMediaZoom() {
  const zoomElements = document.querySelectorAll('.product-media-zoom');
  zoomElements.forEach((element) => {
    if (!element.dataset.zoomInitialized) {
      element.dataset.zoomInitialized = 'true';
      new ProductMediaZoom(element);
    }
  });
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeProductMediaZoom);
} else {
  initializeProductMediaZoom();
}
